import React, { useMemo, useState } from 'react';

import { Flex } from '@reckon-web/flex';
import { Toggle } from '@reckon-web/toggle';
import { FieldLabel } from '@reckon-web/field';
import { Button } from '@reckon-web/button';
import { Field } from '@reckon-web/field';
import { SearchInput } from '@reckon-web/search-input';
import { Inline } from '@reckon-web/inline';
import { useTheme } from '@reckon-web/theme';
import { makeId } from '@reckon-web/utils';
import { ConfigMenu, ConfigMenuGroup } from '@reckon-web/config-menu';

import { useGetMembersList } from '../../../data/useGetMembersList';
import { taskStatuses } from '../../../globals';
import {
  TaskStatusBadge,
  getTaskStatusLabel,
} from '../../common/task-status-badge';
import { MultiFilterMenu } from '../../common/multi-filter-menu';
import { DateRangePicker } from '../../common/date-range-picker';
import {
  VisibleFilters as VisibleFiltersType,
  useVisibleFilters,
} from '../../common/visible-filters';
import { JobTasksFilter } from '../../work/filter-helpers';
import { filterUserList } from '../../work/tasks/filter';
import { DisabledElementTooltip } from '../../common/disabled-element-tooltip';
import {
  GetFamilyGroupsByIdsQueryVariables,
  useGetFamilyGroupsByIds,
} from '../../../data/useGetFamilyGroupsByIds';
import { Task } from '../../../hooks/use-query-tasks';
import { ensureArray, isDefined } from '../../../utils/array-utils';

const statusOptions = taskStatuses.map(({ id }) => ({
  label: getTaskStatusLabel(id),
  value: id,
  render: <TaskStatusBadge status={id} />,
}));

const tasksVisibleFilters: {
  label: string;
  value: keyof VisibleFiltersType;
  disabled: boolean;
}[] = [
  { label: 'Family group', value: 'allFamilyGroup', disabled: false },
  { label: 'Task completed date', value: 'allCompletedDate', disabled: false },
];

export const Filter: React.FC<{
  filter: JobTasksFilter;
  setFilter: (value: Partial<JobTasksFilter>) => void;
  clearFilter: () => void;
  filtered: boolean;
  tasks: Task[] | undefined;
  testId?: string;
}> = ({
  filter,
  setFilter,
  clearFilter,
  filtered,
  tasks,
  testId = `job__tasks__filter`,
}) => {
  const theme = useTheme();
  const [visibleFilters, setVisibleFilters] = useVisibleFilters();

  const users = useGetMembersList();

  const entitiesOptions = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return [];
    }
    const entities = tasks.map((x) => x.entity).filter(isDefined);
    if (entities.length === 0) {
      return [];
    }
    const distinctEntities = [
      ...new Map(entities.map((x) => [x.cloudId, x])).values(),
    ];
    return distinctEntities.map((x) => ({
      label: x.name || '',
      value: x.cloudId || '',
    }));
  }, [tasks]);

  const familyGroupArgs: GetFamilyGroupsByIdsQueryVariables | undefined =
    useMemo(() => {
      if (!tasks || tasks.length === 0) {
        return undefined;
      }
      const cloudIds = tasks
        .map((x) => x.entity?.familyGroupId)
        .filter((x): x is string => typeof x === 'string');
      if (cloudIds.length === 0) {
        return undefined;
      }
      return { cloudIds: [...new Set(cloudIds)] };
    }, [tasks]);

  const {
    data: familyGroupsData,
    // loading: familyGroupsLoading,
  } = useGetFamilyGroupsByIds({
    variables: familyGroupArgs,
    skip: !familyGroupArgs,
  });

  const familyGroupsOptions = useMemo(
    () =>
      familyGroupsData?.map((x) => ({
        label: x.name || '',
        value: x.cloudId || '',
      })) || [],
    [familyGroupsData]
  );

  const [staffSearchTerm, setStaffSearchTerm] = useState<string>('');
  const staffOptions = useMemo(() => {
    return filterUserList(users.data, staffSearchTerm);
  }, [users.data, staffSearchTerm]);

  if (users.error) return <div>Error!</div>;

  return (
    <Flex gap="medium" alignItems="flex-start" data={{ testid: testId }}>
      <Inline alignY="top" gap="small">
        <Field label="Filter" labelVisible={false}>
          <SearchInput
            placeholder="Filter..."
            size="small"
            onChange={(e) => setFilter({ text: e.target.value })}
            onClear={() => setFilter({ text: '' })}
            value={filter.text || ''}
            css={{
              width: '128px',
              // FIXME: GAP
              // Overriding with borderHovered in order for input to stand out from the muted background colour
              border: `1px ${theme.palette.formInput.borderHovered} solid`,
            }}
            data-testid={makeId(testId, 'search')}
          />
        </Field>
        <Flex
          gap="xsmall"
          paddingX="xsmall"
          alignItems="center"
          height={theme.sizing.small}
        >
          <Toggle
            size="small"
            id="job-tasks-my-work-toggle"
            checked={Boolean(filter.myWork)}
            onChange={(myWork) => setFilter({ myWork })}
            data-testid={makeId(testId, 'my-work-toggle')}
          />
          <FieldLabel
            htmlFor="job-tasks-my-work-toggle"
            data-testid={makeId(testId, 'my-wor-label')}
          >
            My work
          </FieldLabel>
        </Flex>
        <Flex
          gap="xsmall"
          paddingX="xsmall"
          alignItems="center"
          height={theme.sizing.small}
        >
          <Toggle
            size="small"
            id="job-tasks-overdue-toggle"
            checked={Boolean(filter.dueDateOverdue)}
            onChange={(dueDateOverdue) => setFilter({ dueDateOverdue })}
            data-testid={makeId(testId, 'overdue-oggle')}
          />
          <FieldLabel
            htmlFor="job-tasks-overdue-toggle"
            data-testid={makeId(testId, 'overdue-label')}
          >
            Overdue
          </FieldLabel>
        </Flex>
        <MultiFilterMenu
          label="Client"
          options={entitiesOptions}
          values={ensureArray(filter.entities)}
          setValues={(entities) => setFilter({ entities })}
          testId={makeId(testId, 'client')}
        />
        <DisabledElementTooltip
          label="Staff select dropdown"
          content="Cannot select individual users when My Work is on"
          childDisabled={Boolean(filter.myWork)}
        >
          <MultiFilterMenu
            label="Staff"
            options={staffOptions}
            values={ensureArray(filter.staff)}
            onInputChanged={(inputValue?: string) =>
              setStaffSearchTerm(inputValue || '')
            }
            setValues={(staff) => setFilter({ staff })}
            disabled={Boolean(filter.myWork)}
            testId={makeId(testId, 'staff')}
          />
        </DisabledElementTooltip>
        <MultiFilterMenu
          label="Status"
          options={statusOptions}
          moveSelectionsToTop={false}
          values={filter.statuses || []}
          setValues={(statuses) => setFilter({ statuses })}
          testId={makeId(testId, 'status')}
        />
        <DisabledElementTooltip
          label="Due date range picker"
          content="Cannot select date when overdue is on"
          childDisabled={Boolean(filter.dueDateOverdue)}
        >
          <DateRangePicker
            label="Due"
            value={{ start: filter.dueDateBegin, end: filter.dueDateEnd }}
            onChange={({ start: dueDateBegin, end: dueDateEnd }) =>
              setFilter({ dueDateBegin, dueDateEnd })
            }
            disabled={Boolean(filter.dueDateOverdue)}
            testId={makeId(testId, 'due')}
          />
        </DisabledElementTooltip>
        {visibleFilters.allFamilyGroup && (
          <MultiFilterMenu
            label="Family group"
            options={familyGroupsOptions}
            values={ensureArray(filter.familyGroupIds)}
            setValues={(familyGroupIds) => setFilter({ familyGroupIds })}
            testId={makeId(testId, 'family-group')}
          />
        )}
        {visibleFilters.allCompletedDate && (
          <DateRangePicker
            label="Completed"
            value={{
              start: filter.completedDateBegin,
              end: filter.completedDateEnd,
            }}
            onChange={({ start: completedDateBegin, end: completedDateEnd }) =>
              setFilter({ completedDateBegin, completedDateEnd })
            }
            testId={makeId(testId, 'completed')}
          />
        )}
        {filtered && (
          <Button
            size="small"
            weight="none"
            tone="passive"
            label="Clear"
            onClick={clearFilter}
            data-testid={makeId(testId, 'clear')}
          />
        )}
        <ConfigMenu trigger="More">
          <ConfigMenuGroup
            title="Task filters"
            onChange={(x) =>
              setVisibleFilters((prev: VisibleFiltersType) => ({
                ...prev,
                ...Object.fromEntries(
                  tasksVisibleFilters.map<[keyof VisibleFiltersType, boolean]>(
                    ({ value, disabled }) => [
                      value,
                      disabled || x.includes(value),
                    ]
                  )
                ),
              }))
            }
            options={tasksVisibleFilters}
            value={(
              Object.entries(visibleFilters) as [
                keyof VisibleFiltersType,
                boolean,
              ][]
            )
              .filter(([, v]) => v)
              .map(([k]) => k)}
            data-testid={makeId(testId, 'more-filters-tasks')}
          />
        </ConfigMenu>
      </Inline>
    </Flex>
  );
};
